import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Clock,
  FileText,
  Building2,
  MapPin,
  ArrowRight,
  BarChart3,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader, MetricCard } from '@/components/common';
import {
  rateAnalyticsApi,
  RateAnalyticsSummary,
  RatesByCarrier,
  RatesByLane,
  ExpiringRate,
  RateTrendData,
  TransportMode,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart,
} from 'recharts';

// Chart colors
const MODE_COLORS: Record<TransportMode, string> = {
  TruckFTL: '#3B82F6',
  TruckLTL: '#60A5FA',
  Ocean: '#10B981',
  Air: '#8B5CF6',
  Rail: '#F59E0B',
  Intermodal: '#6366F1',
};

const COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#6366F1', '#14B8A6', '#EC4899'];

const EXPIRY_DAYS = [
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 30, label: '30 Days' },
  { value: 60, label: '60 Days' },
];

export default function RateAnalytics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);

  // Data state
  const [summary, setSummary] = useState<RateAnalyticsSummary | null>(null);
  const [carrierStats, setCarrierStats] = useState<RatesByCarrier[]>([]);
  const [laneStats, setLaneStats] = useState<RatesByLane[]>([]);
  const [expiringRates, setExpiringRates] = useState<ExpiringRate[]>([]);
  const [trends, setTrends] = useState<RateTrendData[]>([]);

  // Filters
  const activeTab = searchParams.get('tab') || 'carrier';
  const expiryDays = parseInt(searchParams.get('days') || '30') as 7 | 14 | 30 | 60;
  const modeFilter = searchParams.get('mode') as TransportMode | undefined;

  const setFilter = (key: string, value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  // Load initial data
  useEffect(() => {
    const loadSummary = async () => {
      try {
        const [summaryData, trendsData] = await Promise.all([
          rateAnalyticsApi.getSummary(),
          rateAnalyticsApi.getTrends({ months: 6 }),
        ]);
        setSummary(summaryData);
        setTrends(trendsData);
      } catch (error) {
        toast.error(getErrorMessage(error));
      }
    };

    loadSummary();
  }, []);

  // Load tab-specific data
  const loadTabData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'carrier') {
        const data = await rateAnalyticsApi.getByCarrier({ mode: modeFilter, limit: 20 });
        setCarrierStats(data);
      } else if (activeTab === 'lane') {
        const data = await rateAnalyticsApi.getByLane({ mode: modeFilter, limit: 20 });
        setLaneStats(data);
      } else if (activeTab === 'expiring') {
        const data = await rateAnalyticsApi.getExpiring(expiryDays);
        setExpiringRates(data);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [activeTab, modeFilter, expiryDays]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Rate Analytics"
        description="Detailed analysis of rate performance and trends"
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard
          label="Total Rates"
          value={formatNumber(summary?.totalRates || 0)}
          icon={<FileText className="w-5 h-5" />}
        />
        <MetricCard
          label="Active Rates"
          value={formatNumber(summary?.activeRates || 0)}
          icon={<FileText className="w-5 h-5 text-emerald-500" />}
        />
        <MetricCard
          label="Expiring Soon"
          value={formatNumber(summary?.expiringRates || 0)}
          icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}
        />
        <MetricCard
          label="Contract Rates"
          value={formatNumber(summary?.contractRates || 0)}
          icon={<FileText className="w-5 h-5 text-blue-500" />}
        />
        <MetricCard
          label="Avg Rate"
          value={formatCurrency(summary?.avgRate || 0)}
          icon={<DollarSign className="w-5 h-5 text-emerald-500" />}
        />
        <MetricCard
          label="Rate Trend"
          value={`${(summary?.ratesTrend || 0) > 0 ? '+' : ''}${summary?.ratesTrend || 0}%`}
          icon={
            (summary?.ratesTrend || 0) >= 0 ? (
              <TrendingUp className="w-5 h-5 text-emerald-500" />
            ) : (
              <TrendingDown className="w-5 h-5 text-red-500" />
            )
          }
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Rate Trends Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Rate Trends (6 Months)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trends.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) =>
                        new Date(v).toLocaleDateString('en-US', { month: 'short' })
                      }
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) => `$${v.toLocaleString()}`}
                    />
                    <Tooltip
                      labelFormatter={(label) =>
                        new Date(label).toLocaleDateString('en-US', {
                          month: 'long',
                          year: 'numeric',
                        })
                      }
                      formatter={(value: number, name: string) => [
                        formatCurrency(value),
                        name === 'avgRate' ? 'Average' : name === 'minRate' ? 'Min' : 'Max',
                      ]}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="maxRate"
                      name="Max"
                      fill="#EF444420"
                      stroke="#EF4444"
                      strokeWidth={1}
                    />
                    <Area
                      type="monotone"
                      dataKey="avgRate"
                      name="Average"
                      fill="#3B82F620"
                      stroke="#3B82F6"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="minRate"
                      name="Min"
                      fill="#10B98120"
                      stroke="#10B981"
                      strokeWidth={1}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No trend data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rates by Mode Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Distribution by Mode</CardTitle>
          </CardHeader>
          <CardContent>
            {summary?.byMode && summary.byMode.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={summary.byMode}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="mode"
                    >
                      {summary.byMode.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={MODE_COLORS[entry.mode] || COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string, props) => [
                        `${formatNumber(value)} (${formatCurrency(props.payload.avgRate)} avg)`,
                        name,
                      ]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabbed Analysis */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={(v) => setFilter('tab', v)}>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <TabsList>
                <TabsTrigger value="carrier" className="gap-2">
                  <Building2 className="w-4 h-4" />
                  By Carrier
                </TabsTrigger>
                <TabsTrigger value="lane" className="gap-2">
                  <MapPin className="w-4 h-4" />
                  By Lane
                </TabsTrigger>
                <TabsTrigger value="expiring" className="gap-2">
                  <Clock className="w-4 h-4" />
                  Expiring
                </TabsTrigger>
              </TabsList>

              {activeTab !== 'expiring' && (
                <Select
                  value={modeFilter || ''}
                  onValueChange={(v) => setFilter('mode', v || undefined)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Modes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Modes</SelectItem>
                    <SelectItem value="TruckFTL">Truck FTL</SelectItem>
                    <SelectItem value="TruckLTL">Truck LTL</SelectItem>
                    <SelectItem value="Ocean">Ocean</SelectItem>
                    <SelectItem value="Air">Air</SelectItem>
                    <SelectItem value="Rail">Rail</SelectItem>
                    <SelectItem value="Intermodal">Intermodal</SelectItem>
                  </SelectContent>
                </Select>
              )}

              {activeTab === 'expiring' && (
                <div className="flex gap-2">
                  {EXPIRY_DAYS.map((d) => (
                    <Button
                      key={d.value}
                      variant={expiryDays === d.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilter('days', d.value.toString())}
                    >
                      {d.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* By Carrier Tab */}
            <TabsContent value="carrier">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : carrierStats.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/50 text-left">
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Carrier
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                          Total Rates
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                          Active
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                          Avg Rate
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                          Min
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                          Max
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {carrierStats.map((carrier) => (
                        <tr key={carrier.carrierId} className="hover:bg-muted/50">
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium">{carrier.carrierName}</div>
                              <div className="text-sm text-muted-foreground">{carrier.carrierCode}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {formatNumber(carrier.totalRates)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Badge variant="outline" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                              {formatNumber(carrier.activeRates)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-emerald-600">
                            {formatCurrency(carrier.avgRate)}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground">
                            {formatCurrency(carrier.minRate)}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground">
                            {formatCurrency(carrier.maxRate)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No carrier data available</p>
                </div>
              )}
            </TabsContent>

            {/* By Lane Tab */}
            <TabsContent value="lane">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : laneStats.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/50 text-left">
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Lane
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                          Rates
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                          Avg Rate
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                          Min
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                          Max
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                          Carriers
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {laneStats.map((lane, idx) => (
                        <tr key={idx} className="hover:bg-muted/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{lane.originPort}</Badge>
                              <ArrowRight className="w-4 h-4 text-muted-foreground" />
                              <Badge variant="outline">{lane.destinationPort}</Badge>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {formatNumber(lane.rateCount)}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-emerald-600">
                            {formatCurrency(lane.avgRate)}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground">
                            {formatCurrency(lane.minRate)}
                          </td>
                          <td className="px-4 py-3 text-right text-muted-foreground">
                            {formatCurrency(lane.maxRate)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Badge variant="secondary">{lane.carriers}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No lane data available</p>
                </div>
              )}
            </TabsContent>

            {/* Expiring Tab */}
            <TabsContent value="expiring">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : expiringRates.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted/50 text-left">
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          SKU
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Carrier
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Lane
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                          Rate
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                          Expires
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                          Days Left
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {expiringRates.map((rate) => (
                        <tr key={rate.id} className="hover:bg-muted/50">
                          <td className="px-4 py-3">
                            <span className="font-mono text-sm font-medium">{rate.sku}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium text-sm">{rate.carrier.name}</div>
                              <div className="text-xs text-muted-foreground">{rate.carrier.code}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 text-sm">
                              <span>{rate.originPort}</span>
                              <ArrowRight className="w-3 h-3 text-muted-foreground" />
                              <span>{rate.destinationPort}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="font-semibold">
                              {formatCurrency(rate.baseRate)}
                            </div>
                            <div className="text-xs text-muted-foreground">{rate.currency}</div>
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-muted-foreground">
                            {new Date(rate.validTo).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Badge
                              variant="outline"
                              className={
                                rate.daysUntilExpiry <= 7
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                  : rate.daysUntilExpiry <= 14
                                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              }
                            >
                              {rate.daysUntilExpiry}d
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No rates expiring in the next {expiryDays} days</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
